import { i18n, i18nNsFactory, LANGUAGE_LABELS } from '@atlas-frontend/i18n';
import { useCallback } from 'react';
import {
  SystemClassifierBasicDto,
  SystemClassifierItemDto,
} from './schema/types';

const SYSTEM_CLASSIFIERS_NAMESPACE = 'system-classifiers';

const { t, useTranslation } = i18nNsFactory(SYSTEM_CLASSIFIERS_NAMESPACE);

type TranslatableSystemClassifier = SystemClassifierBasicDto & {
  systemClassifierItems?: SystemClassifierItemDto[];
};

const translateText = (
  language: string,
  key: string,
  fallback: string | null,
) => {
  if (language === LANGUAGE_LABELS) return t(key);

  const translation = i18n.getResource(
    language,
    SYSTEM_CLASSIFIERS_NAMESPACE,
    key,
    { keySeparator: false },
  );

  return typeof translation === 'string' ? translation : fallback;
};

export const translateSystemClassifier = <
  Classifier extends TranslatableSystemClassifier,
>(
  systemClassifier: Classifier,
  language: string,
): Classifier => {
  const systemClassifierItems = systemClassifier.systemClassifierItems?.map(
    (item) => ({
      ...item,
      name: translateText(
        language,
        `${systemClassifier.code}.${item.code}`,
        item.name,
      ),
    }),
  );

  return {
    ...systemClassifier,
    name: translateText(language, systemClassifier.code, systemClassifier.name),
    description: translateText(
      language,
      `${systemClassifier.code}.description`,
      systemClassifier.description,
    ),
    ...(systemClassifierItems ? { systemClassifierItems } : {}),
  };
};

export const useSystemClassifierTranslations = () => {
  const { i18n: i18nInstance } = useTranslation();
  const language = i18nInstance.resolvedLanguage ?? i18nInstance.language;

  const translate = useCallback(
    <Classifier extends TranslatableSystemClassifier>(
      systemClassifier: Classifier,
    ) => translateSystemClassifier(systemClassifier, language),
    [language],
  );

  return { translateSystemClassifier: translate };
};
